import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";
import { nodeEnvSchema, vercelEnvSchema, zendeskEnvSchema } from "./env";

const featureFlagSchema = z.enum(["enable", "disable"]).default("disable");

export const clientEnv = createEnv({
  client: {
    NEXT_PUBLIC_NODE_ENV: nodeEnvSchema,
    /** アプリのドメインが以下の環境で設定されている想定です。
     *  - Production環境
     *  - Preview環境(develop)
     *  - local環境 */
    NEXT_PUBLIC_APP_DOMAIN: z.string().min(1).optional(),
    /** local環境では undefined. https://vercel.com/docs/projects/environment-variables/system-environment-variables#system-environment-variables */
    NEXT_PUBLIC_VERCEL_URL: z.string().min(1).optional(),
    /** https://vercel.com/docs/projects/environment-variables/system-environment-variables#system-environment-variables */
    NEXT_PUBLIC_VERCEL_ENV: vercelEnvSchema,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().min(1).optional(),
    NEXT_PUBLIC_SUPABASE_URL: z.string().min(1),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
    NEXT_PUBLIC_ZENDESK_URL: z.url(),
    NEXT_PUBLIC_ZENDESK_JWT_BASE_URL: z.url(),
    NEXT_PUBLIC_ZENDESK_MEMBERS_URL: z.url(),
    NEXT_PUBLIC_ZENDESK_EMPLOYERS_URL: z.url(),
    NEXT_PUBLIC_ZENDESK_ENV: zendeskEnvSchema,
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_FROM: z.iso
      .datetime({ offset: true })
      .optional(),
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_TO: z.iso
      .datetime({ offset: true })
      .optional(),
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_COMMENT: z
      .string()
      .min(1)
      .optional(),
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().optional(),
    NEXT_PUBLIC_GA4_MEASUREMENT_ID_MEMBERS: z.string().optional(),
    NEXT_PUBLIC_GA4_MEASUREMENT_ID_EMPLOYERS: z.string().optional(),
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: z.string(),
    NEXT_PUBLIC_FEATURE_FLAG_RECAPTCHA_FOR_MEMBERS_SIGNUP: z
      .string()
      .optional(),
    NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) return 10;
        const parsed = parseInt(val, 10);
        if (isNaN(parsed) || parsed < 0 || parsed > 100) {
          throw new Error(
            "NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE must be a number between 0 and 100",
          );
        }
        return parsed;
      }),
    NEXT_PUBLIC_SENDGRID_EMAIL_FROM: z.email(),
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_INTERVIEW_QUESTION_ID_ENV:
      featureFlagSchema,
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_RESULT_INFO_ENV:
      featureFlagSchema,
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_FACILITY_FEE_SIMULATION_DEBUG_ENV:
      featureFlagSchema,
    NEXT_PUBLIC_DEBUG_FLAG_COPY_STRESS_CHECK_ANSWER_URL_ENV: featureFlagSchema,
    NEXT_PUBLIC_FEATURE_FLAG_BENEFIT_NOTIFICATION_ENV: featureFlagSchema,
    NEXT_PUBLIC_FEATURE_FLAG_STRESS_CHECK: featureFlagSchema,
    NEXT_PUBLIC_FEATURE_FLAG_HENNYUUJIKENNIN: featureFlagSchema,
    NEXT_PUBLIC_FEATURE_FLAG_RYOYOHITATEKAE_RECEIPT_EXTRACTION:
      featureFlagSchema,
    NEXT_PUBLIC_FEATURE_FLAG_MEDICAL_CHECKUP_RESULTS: featureFlagSchema,

    /** パスベースルーティングの有効化フラグ（next.config.tsでビルド時に設定） */
    NEXT_PUBLIC_PATH_BASED_ROUTING: z.enum(["true", "false"]).default("false"),
    /**
     * ローカルで HTTP 起動するとき
     * NEXT_PUBLIC_VERCEL_ENV=local のときのみ有効（本番・Preview では無視される）。
     */
    NEXT_PUBLIC_LOCAL_USE_HTTP: z.enum(["true", "false"]).optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_APP_DOMAIN: process.env.NEXT_PUBLIC_APP_DOMAIN,
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA:
      process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_ZENDESK_URL: process.env.NEXT_PUBLIC_ZENDESK_URL,
    NEXT_PUBLIC_ZENDESK_JWT_BASE_URL:
      process.env.NEXT_PUBLIC_ZENDESK_JWT_BASE_URL,
    NEXT_PUBLIC_ZENDESK_MEMBERS_URL:
      process.env.NEXT_PUBLIC_ZENDESK_MEMBERS_URL,
    NEXT_PUBLIC_ZENDESK_EMPLOYERS_URL:
      process.env.NEXT_PUBLIC_ZENDESK_EMPLOYERS_URL,
    NEXT_PUBLIC_ZENDESK_ENV: process.env.NEXT_PUBLIC_ZENDESK_ENV,
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_FROM:
      process.env.NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_FROM,
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_TO:
      process.env.NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_TO,
    NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_COMMENT:
      process.env.NEXT_PUBLIC_DIGITAL_IDENTIFICATION_APP_MAINTENANCE_COMMENT,
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN:
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATADOG_APPLICATION_ID:
      process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    NEXT_PUBLIC_GA4_MEASUREMENT_ID_MEMBERS:
      process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID_MEMBERS,
    NEXT_PUBLIC_GA4_MEASUREMENT_ID_EMPLOYERS:
      process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID_EMPLOYERS,
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    NEXT_PUBLIC_FEATURE_FLAG_RECAPTCHA_FOR_MEMBERS_SIGNUP:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_RECAPTCHA_FOR_MEMBERS_SIGNUP,
    NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE:
      process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
    NEXT_PUBLIC_SENDGRID_EMAIL_FROM:
      process.env.NEXT_PUBLIC_SENDGRID_EMAIL_FROM,
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_INTERVIEW_QUESTION_ID_ENV:
      process.env
        .NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_INTERVIEW_QUESTION_ID_ENV,
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_RESULT_INFO_ENV:
      process.env.NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_RESULT_INFO_ENV,
    NEXT_PUBLIC_DEBUG_FLAG_SHOW_FACILITY_FEE_SIMULATION_DEBUG_ENV:
      process.env.NEXT_PUBLIC_DEBUG_FLAG_SHOW_FACILITY_FEE_SIMULATION_DEBUG_ENV,
    NEXT_PUBLIC_DEBUG_FLAG_COPY_STRESS_CHECK_ANSWER_URL_ENV:
      process.env.NEXT_PUBLIC_DEBUG_FLAG_COPY_STRESS_CHECK_ANSWER_URL_ENV,
    NEXT_PUBLIC_FEATURE_FLAG_BENEFIT_NOTIFICATION_ENV:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_BENEFIT_NOTIFICATION_ENV,
    NEXT_PUBLIC_FEATURE_FLAG_STRESS_CHECK:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_STRESS_CHECK,
    NEXT_PUBLIC_FEATURE_FLAG_HENNYUUJIKENNIN:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_HENNYUUJIKENNIN,
    NEXT_PUBLIC_FEATURE_FLAG_RYOYOHITATEKAE_RECEIPT_EXTRACTION:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_RYOYOHITATEKAE_RECEIPT_EXTRACTION,
    NEXT_PUBLIC_FEATURE_FLAG_MEDICAL_CHECKUP_RESULTS:
      process.env.NEXT_PUBLIC_FEATURE_FLAG_MEDICAL_CHECKUP_RESULTS,

    NEXT_PUBLIC_PATH_BASED_ROUTING: process.env.NEXT_PUBLIC_PATH_BASED_ROUTING,
    NEXT_PUBLIC_LOCAL_USE_HTTP: process.env.NEXT_PUBLIC_LOCAL_USE_HTTP,
  },
});
